import { apply, celebrate, give, ITEM_ICONS, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import type { ItemKey } from '../core/state';
import { m } from '../i18n';
import { itemInfo, npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { say as speak, VOICES } from '../audio/speech';
import { say } from '../ui/dialog';
import { h } from '../ui/dom';
import { layers } from '../ui/layers';
import { closePanel, openPanel, refreshPanel } from '../ui/panel';
import { openWallet } from '../ui/wallet';
import { logJournal } from './journal';
import {
  ALLOWANCE,
  addWish,
  deposit,

  life,
  logSpend,
  makeAd,
  needOrWant,
  pay,
  payAllowance,
  payInterest,
  resolveWishes,
  SPLITS,
  withdraw,
  type AdOffer,
} from './money';

/**
 * Run `fn` once no dialog or panel is open, one at a time (day-start events
 * shouldn't trample each other or other packages' morning dialogs).
 */
const queue: { at: number; fn: () => void }[] = [];
let pumping = 0;
export function whenFree(fn: () => void, delay = 600): void {
  queue.push({ at: Date.now() + delay, fn });
  if (!pumping) pumping = window.setInterval(pump, 700);
}
function pump(): void {
  if (layers.blocking()) return;
  const i = queue.findIndex((q) => q.at <= Date.now());
  if (i < 0) {
    if (!queue.length) {
      window.clearInterval(pumping);
      pumping = 0;
    }
    return;
  }
  const [q] = queue.splice(i, 1);
  q.fn();
}

/** Away from home, Mom's money talk arrives by phone. */
const momName = () => `${npcName('mom') || m().life.mom}${S().player.map === 'home' ? '' : ' 📱'}`;

// --- Allowance ---------------------------------------------------------------------------------

const split = { spend: 50, piggy: 30, gift: 20 };

export function allowanceDialog(): void {
  const t = m().life.allowance;
  speak(t.text.replace(/\*\*/g, ''), VOICES.mom);
  say({ who: momName(), icon: '💰', text: t.text, choices: [{ text: t.split, run: openAllowance }] });
}

function openAllowance(): void {
  openPanel('lifeAllowance', () => {
    const t = m().life.allowance;
    const left = ALLOWANCE - split.spend - split.piggy - split.gift;
    const row = (key: keyof typeof split, label: string) =>
      h(
        'div',
        { class: `life-split-row ${key}` },
        h('span', { class: 'lbl' }, label),
        h('button', { class: 'life-step', disabled: split[key] <= 0, onclick: () => ((split[key] -= 10), sfx('click'), refreshPanel('lifeAllowance')) }, '−'),
        h('b', { class: 'val' }, `${split[key]}🪙`),
        h('button', { class: 'life-step', disabled: left <= 0, onclick: () => ((split[key] += 10), sfx('click'), refreshPanel('lifeAllowance')) }, '+'),
      );
    return {
      title: t.title,
      className: 'life-allow',
      closable: false,
      body: [
        h('p', { class: 'hint' }, t.hint),
        h(
          'div',
          { class: 'chips' },
          (Object.keys(SPLITS) as (keyof typeof SPLITS)[]).map((k) =>
            h(
              'button',
              {
                class: `chip small${SPLITS[k][0] === split.spend && SPLITS[k][1] === split.piggy ? ' on' : ''}`,
                onclick: () => {
                  [split.spend, split.piggy, split.gift] = SPLITS[k];
                  sfx('pop');
                  refreshPanel('lifeAllowance');
                },
              },
              t.presets[k],
            ),
          ),
        ),
        h(
          'div',
          { class: 'life-split-bar' },
          h('i', { class: 'spend', style: `flex:${split.spend}` }),
          h('i', { class: 'piggy', style: `flex:${split.piggy}` }),
          h('i', { class: 'gift', style: `flex:${split.gift}` }),
          left > 0 ? h('i', { class: 'left', style: `flex:${left}` }) : null,
        ),
        row('spend', t.spend),
        row('piggy', t.piggy),
        row('gift', t.gift),
        h('p', { class: `hint big ${left === 0 ? 'ok' : 'bad'}` }, t.left(left)),
      ],
      footer: [
        h(
          'button',
          {
            class: 'btn gold',
            disabled: left !== 0,
            onclick: () => {
              if (!payAllowance(split.spend, split.piggy, split.gift)) return;
              closePanel('lifeAllowance');
              sfx('checkout');
              celebrate();
              logJournal('💰', 'life.journal.allowance', `${split.spend}/${split.piggy}/${split.gift}`);
              const text = split.piggy >= 40 ? t.momSaver : split.spend >= 70 ? t.momSpender : t.momBalanced;
              if (split.piggy >= 30) apply({ stars: 3, love: { mom: 4 } }, { float: true });
              speak(text, VOICES.mom);
              say({ who: momName(), icon: '😊', text });
            },
          },
          t.confirm,
        ),
      ],
    };
  });
}

export function interestDialog(): void {
  const add = payInterest();
  const t = m().life.interest;
  if (add > 0) {
    sfx('checkout');
    say({ who: momName(), icon: '🐷', text: t.text(add, S().money.piggy) });
  } else if (S().money.piggy === 0) say({ who: momName(), icon: '🐷', text: t.empty });
}

// --- Piggy bank ---------------------------------------------------------------------------------

export function piggyBank(): void {
  const t = m().life.piggy;
  const s = S();
  const amounts = [10, 50].filter((n) => s.coins >= n);
  say({
    who: objName('life_piggy'),
    icon: '🐷',
    text: t.text(s.money.piggy, s.coins),
    choices: [
      ...amounts.map((n) => ({ text: t.in(n), run: () => piggyMove(n) })),
      ...(s.coins > 0 && !amounts.includes(s.coins) ? [{ text: `${t.all} (${s.coins}🪙)`, run: () => piggyMove(s.coins) }] : []),
      ...[10, 50]
        .filter((n) => s.money.piggy >= n)
        .map((n) => ({
          text: t.out(n),
          run: () => {
            withdraw(n);
            sfx('pickup');
            say({ who: objName('life_piggy'), icon: '🐷', text: `${t.done(S().money.piggy)}\n${t.outWarn}`, choices: [{ text: t.wallet, run: openWallet }, { text: m().common.ok }] });
          },
        })),
      { text: t.wallet, run: openWallet },
      { text: m().common.back },
    ],
  });
}

function piggyMove(n: number): void {
  if (!deposit(n)) return;
  sfx('pickup');
  if (onceToday('life.piggySave')) apply({ stars: 1, mood: 2 });
  say({ who: objName('life_piggy'), icon: '🐷', text: m().life.piggy.done(S().money.piggy), choices: [{ text: m().common.ok }, { text: m().life.piggy.in(10), run: piggyBank }] });
}

// --- Needs vs wants -----------------------------------------------------------------------------

/**
 * A purchase: the first one of the day asks "need or want?" first (reward if right),
 * then pays (gift fund first in the gift shop), logs the spending and plays the checkout sound.
 */
export function buyWithQuestion(
  opts: { item?: ItemKey; label: string; kind: 'need' | 'want'; price: number; useGift?: boolean; who: string; split?: { need: number; want: number } },
  done: () => void,
): void {
  const go = () => {
    if (!pay(opts.price, opts.useGift)) return toast(m().shop.poor);
    sfx('checkout');
    if (opts.split) {
      logSpend('need', opts.split.need);
      logSpend('want', opts.split.want);
    } else logSpend(opts.kind, opts.price);
    done();
  };
  if (S().daily['life.nwAsked']) return go();
  S().daily['life.nwAsked'] = true;
  const t = m().life.nw;
  const answer = (pick: 'need' | 'want') => {
    const right = pick === opts.kind;
    const kindName = m().life[opts.kind];
    if (right) {
      life().nwRight++;
      sfx('ok');
      apply({ stars: 2 });
    } else sfx('bad');
    say({
      who: t.title,
      icon: right ? '✅' : '💭',
      text: `${right ? t.right(opts.label, kindName) : t.wrong(opts.label, kindName)}\n${opts.kind === 'need' ? t.needWhy : t.wantWhy}`,
      choices: [{ text: `${t.buy} (${opts.price}🪙)`, run: go }, { text: m().common.cancel }],
    });
  };
  say({
    who: t.title,
    icon: '🤔',
    text: t.ask(opts.label),
    choices: [
      { text: `🧺 ${m().life.need}`, run: () => answer('need') },
      { text: `🎈 ${m().life.want}`, run: () => answer('want') },
    ],
  });
}

export function buyItem(item: ItemKey, price: number, who: string, useGift: boolean, done: () => void): void {
  buyWithQuestion({ item, label: itemInfo(item).name, kind: needOrWant(item), price, useGift, who }, done);
}

// --- Impulse ads ----------------------------------------------------------------------------------

let adTimer = 0;

/** A flashy "LIMITED OFFER!" pop-up from the TV, a mall screen or the phone. */
export function impulseAd(source: 'tv' | 'mall' | 'phone', offer: AdOffer = makeAd()): void {
  window.clearInterval(adTimer);
  let secs = 10;
  const t = m().life.ad;
  sfx('ring');
  openPanel(
    'lifeAd',
    () => {
      const name = itemInfo(offer.item).name;
      return {
        title: t.sources[source],
        className: 'life-ad',
        body: [
          h(
            'div',
            { class: 'life-ad-card' },
            h('div', { class: 'life-ad-flash' }, t.flash),
            h('div', { class: 'life-ad-item' }, ITEM_ICONS[offer.item]),
            h('b', { class: 'life-ad-name' }, name),
            h('div', { class: 'life-ad-prices' }, h('s', null, t.was(offer.was)), h('span', null, t.now(offer.now))),
            h('div', { class: 'life-ad-only' }, t.only),
            h('small', { class: 'life-ad-timer' }, t.timer(secs)),
          ),
        ],
        footer: [
          h(
            'button',
            {
              class: 'btn red',
              onclick: () => {
                window.clearInterval(adTimer);
                closePanel('lifeAd');
                if (!pay(offer.now)) return toast(t.poor);
                sfx('checkout');
                give(offer.item, 1);
                logSpend('want', offer.now);
                say({ who: t.sources[source], icon: '🧐', text: t.boughtLesson(offer.was, offer.now) });
              },
            },
            t.buy(offer.now),
          ),
          h(
            'button',
            {
              class: 'btn green',
              onclick: () => {
                window.clearInterval(adTimer);
                closePanel('lifeAd');
                addWish(offer.item, offer.now);
                celebrate();
                apply({ stars: 2 });
                say({ who: t.sources[source], icon: '⏳', text: t.waitLesson });
              },
            },
            t.wait,
          ),
        ],
      };
    },
    () => window.clearInterval(adTimer),
  );
  // The countdown is part of the trick: it pushes you to decide fast.
  adTimer = window.setInterval(() => {
    secs = Math.max(1, secs - 1);
    const el = document.querySelector('.life-ad-timer');
    if (el) el.textContent = m().life.ad.timer(secs);
    else window.clearInterval(adTimer);
  }, 1000);
}

/** The next morning, each wish turns out cheaper or not needed after all. */
export function wishMorning(rnd: () => number = Math.random): void {
  const out = resolveWishes(rnd);
  const t = m().life.wish;
  const next = (i: number) => {
    const o = out[i];
    if (!o) return;
    const name = itemInfo(o.item).name;
    if (o.kind === 'forgot') {
      apply({ stars: 3, mood: 3 });
      logJournal('⏳', 'life.journal.waited');
      return say({ who: t.title, icon: '💭', text: `${t.forgot(name)}\n\n${t.lesson}`, choices: [{ text: m().common.ok, run: () => next(i + 1) }] });
    }
    say({
      who: t.title,
      icon: '🏷️',
      text: `${t.cheaper(name, o.was, o.price)}\n\n${t.lesson}`,
      choices: [
        {
          text: t.buy(o.price),
          run: () => {
            if (!pay(o.price)) return toast(m().shop.poor);
            sfx('checkout');
            give(o.item, 1);
            logSpend('want', o.price);
            next(i + 1);
          },
        },
        { text: t.skip, run: () => (toast(t.kept), apply({ stars: 1 }), next(i + 1)) },
      ],
    });
  };
  if (out.length) next(0);
}
